import { VoiceboxMessage } from 'vet-bones/constants/voicebox';

import { DataSourceTableSelectItem } from 'src/constants/dataSourceTable';
import { DataSourceType } from 'src/constants/dataSourceTypeDescription';
import { ProjectPropertyType } from 'src/constants/project';
import { ProjectGeoDataByIri } from 'src/constants/projectGeoData';
import { EvaluationSummary } from 'src/constants/voicebox';
import { ModelRule } from 'src/containers/ProjectDiagram/types/data';
import { DiagramSnapshot } from 'src/containers/ProjectDiagram/types/diagram';
import {
  CsvMappingInfo,
  DatabaseInfo,
  ModelAliasInfo,
  ModelInfo,
  SensitivePropertyGroupInfo,
  VirtualGraphMappingInfo,
} from 'src/containers/PublishProjectDrawer/types';
import {
  SqlQuotingConvention,
  VirtualGraphOption,
} from 'src/store/state/dataSources';

export type ProjectFileUploadStatistics = {
  distinct: number;
  records: number;
};

export type ProjectFileUploadField = {
  value: string;
  label: string;
  statistics: ProjectFileUploadStatistics;
};

export type ProjectVirtualGraphField = {
  value: string;
  label: string;
  statistics?: never;
};

export type ProjectResourceField =
  | ProjectFileUploadField
  | ProjectVirtualGraphField;

export type ProjectFileUploadOptions = {
  escape: string;
  header: boolean;
  quote: string;
  separator: string;
  skipEmpty: boolean;
};

export const defaultProjectFileUploadOptions: ProjectFileUploadOptions = {
  escape: '"',
  header: true,
  quote: '"',
  separator: ',',
  skipEmpty: true,
};

export type ProjectFileUpload = {
  id: string;
  path: string;
  name: string;
  contents: string;
  fields: ProjectFileUploadField[];
  options: ProjectFileUploadOptions;
  dateLastUpdated: number;
  /**
   * Is set to true when the uploaded csv file is >10MiB.
   *
   * When true, this means our `contents` only contains the first 25 rows
   * of the uploaded file. (to act as a sample)
   *
   * The user will be prompted during Publish to re-upload the big csv file.
   */
  partial: boolean;
};

export type ProjectVirtualGraphOptions = Record<
  VirtualGraphOption | string,
  any
> & {
  [VirtualGraphOption.SQL_QUOTING_CONVENTION]: SqlQuotingConvention;
  [VirtualGraphOption.SQL_FUNCTIONS]: string;
  [VirtualGraphOption.PERCENT_ENCODE]: boolean;
};

export const defaultProjectVirtualGraphOptions: ProjectVirtualGraphOptions = {
  [VirtualGraphOption.SQL_QUOTING_CONVENTION]: SqlQuotingConvention.NATIVE,
  [VirtualGraphOption.SQL_FUNCTIONS]: '',
  [VirtualGraphOption.PERCENT_ENCODE]: true,
};

export type ProjectVirtualGraph = {
  id: string;
  name: string;
  connectionId: string | null;
  /** not unique! simply the name of the data source */
  dataSourceId: string;
  dataSourceType: DataSourceType | null;
  dataSourceEndpoint?: string;
  /** `string` means this is an sql query */
  from: DataSourceTableSelectItem | string;
  fields: ProjectVirtualGraphField[];
  options: ProjectVirtualGraphOptions;
  previewRows: string[][];
  dateLastUpdated: number;
  allResultsIncluded?: boolean;
};

export type ProjectResource = ProjectFileUpload | ProjectVirtualGraph;

export type ProjectModelClass = {
  iri: string;
  parents: string[];
};

export type ProjectModelProperty = {
  iri: string;
  domain: string[];
  range: string[];
  parents: string[];
  isDatatype?: boolean;
  propertyType: ProjectPropertyType;
};

export type ProjectModelRule = Omit<ModelRule, 'models'>;

export type ProjectModel = {
  connectionId: string | null;
  databaseId: string | null;
  databaseNamespaces: Record<string, string> | null;
  id: string;
  name: string;
  importedModelName?: string;
  dateLastUpdated: number;
  namespace: string;
  voicebox?: boolean;
  draft?: boolean;

  /** These fields only exist during model import */
  classes: Record<string, ProjectModelClass> | null;
  properties: Record<string, ProjectModelProperty> | null;
  colorIdsByIri: Record<string, string> | null;
  descriptionsByIri: Record<string, string> | null;
  geoDataByIri: Record<string, ProjectGeoDataByIri> | null;
  rules: Record<string, ProjectModelRule> | null;
  labelsByIri: Record<string, string> | null;
};

export type PendingModelData = {
  modelName: string;
  classes: Record<string, ProjectModelClass> | null;
  properties: Record<string, ProjectModelProperty> | null;
};

export type PublishHistory = {
  connectionId: string;
  csvResourceHistoriesById: Record<
    string,
    Pick<CsvMappingInfo, 'creationType' | 'namedGraphIri' | 'shouldPublish'>
  >;
  databaseInfo: DatabaseInfo;
  doExportProject: boolean;
  isConsolidatingModels: boolean;
  isPublishingRules: boolean;
  modelAliasHistory?: Pick<ModelAliasInfo, 'modelName' | 'namedGraphIris'>;
  modelHistoriesById: Record<
    string,
    Pick<ModelInfo, 'modelName' | 'namedGraphIri' | 'shouldPublish'>
  >;
  sensitivePropertyGroupsByName: Record<
    string,
    Pick<SensitivePropertyGroupInfo, 'actionType' | 'publishName'>
  >;
  vgResourceHistoriesById: Record<
    string,
    Pick<VirtualGraphMappingInfo, 'virtualGraphName' | 'shouldPublish'>
  >;
};

export const createEmptyPublishHistory = (): PublishHistory => ({
  connectionId: '',
  csvResourceHistoriesById: {},
  databaseInfo: {
    databaseId: '',
    isPublishingSPG: true,
  },
  doExportProject: true,
  isConsolidatingModels: false,
  isPublishingRules: true,
  modelHistoriesById: {},
  sensitivePropertyGroupsByName: {},
  vgResourceHistoriesById: {},
});

export type Project = {
  id: string;
  name: string;
  cloudProjectId?: string;
  cloudProjectLastUpdated?: number;
  connectionId?: string;
  dateCreated: number;
  dateLastUpdated: number;
  description?: string;
  isCloudProject?: boolean;
  isRemoved: boolean;
  diagram: DiagramSnapshot;
  /** This Record can contain deleted resources! */
  fileUploadsById: Record<string, ProjectFileUpload>;
  /** This Record can contain deleted resources! */
  virtualGraphsById: Record<string, ProjectVirtualGraph>;
  /** This Record can contain deleted models! Check `BaseModelData.deletedModelIds` */
  modelsById: Record<string, ProjectModel>;
  dataNamespace: string;
  propertyType: ProjectPropertyType;
  publishHistory: PublishHistory;
  targetModelId: string;
  voiceboxConversationId?: string;
  voiceboxMessageHistory?: VoiceboxMessage[];
  voiceboxEvaluatedCompetencyQuestions?: string[];
  voiceboxEvaluatedSummary?: boolean;
  voiceboxEvaluationSummary?: EvaluationSummary;
};

export class ProjectsState {
  constructor(
    public projectIds: string[] = [],
    public projectsById: Record<string, Project> = {}
  ) {}
}
